import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CallSplit,
  ContentCopy,
  MoreVert,
} from '@mui/icons-material';
import { IconPlus, IconBolt } from '@tabler/icons-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type {
  CustomerIndexItemDto,
  QuotesListItem,
  QuoteStatus,
  QuoteType,
} from '@techgitter/contracts';
import {
  SecondaryToolbar,
  ToolbarSearch,
  ToolbarSelect,
  ToolbarCheckbox,
  ToolbarSpacer,
  ToolbarDivider,
  ToolbarButton,
} from '../components/toolbar';
import {
  DataTableCard,
  DataTable,
  DataTableHeadCell,
  DataTableRow,
  DataTableCell,
  DataTableEmpty,
  DataTablePagination,
} from '../components/table';
import { Chip } from '../components/chip';
import type { ChipTone } from '../components/chip';
import { useCurrentUser } from '../contexts/CurrentUserContext';
import { QuickQuoteDialog } from '../components/quotes/QuickQuoteDialog';
import {
  fetchQuotes,
  createQuote,
  createQuoteVariation,
} from '../services/quotesApi';
import { CustomerLookupControl } from '../components/tickets/Customer/CustomerLookupControl';
import { DuplicateQuoteDialog } from '../components/quotes/DuplicateQuoteDialog';

const QUOTE_TYPE_LABEL: Record<QuoteType, string> = {
  new_laptop: 'New Laptop',
  new_desktop: 'New Desktop',
  new_desktop_custom: 'New Desktop (Custom)',
  new_all_in_one: 'New All-in-One',
  repair: 'Repair Service',
  network_install: 'Network Installation',
  m365: 'M365 Onboarding',
  replacement: 'New Computer',
};

// `replacement` is intentionally excluded — it's kept in the type union
// for back-compat with existing rows but isn't offered for new quotes.
const QUOTE_TYPE_OPTIONS: QuoteType[] = [
  'new_laptop',
  'new_desktop',
  'new_desktop_custom',
  'new_all_in_one',
  'repair',
  'network_install',
  'm365',
];

const QUOTE_STATUS_FILTERS: QuoteStatus[] = [
  'requested',
  'draft',
  'sent',
  'accepted',
  'declined',
  'expired',
];

const QUOTE_STATUS_LABEL: Record<QuoteStatus, string> = {
  requested: 'Requested',
  draft: 'Draft',
  sent: 'Sent',
  accepted: 'Accepted',
  declined: 'Declined',
  expired: 'Expired',
};

// `requested` is included by default so any tech viewing the quotes
// list immediately sees the in-tray of quotes waiting to be picked up.
const DEFAULT_STATUS_FILTER: QuoteStatus[] = ['requested', 'draft', 'sent'];

const fmtCurrency = (n: number): string =>
  n.toLocaleString('en-AU', { style: 'currency', currency: 'AUD' });

const fmtDate = (value: string | null): string => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleDateString('en-AU', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return value;
  }
};

const statusTone = (status: string): ChipTone => {
  switch (status) {
    case 'requested': return 'info';
    case 'draft': return 'neutral';
    case 'sent': return 'info';
    case 'accepted': return 'success';
    case 'declined': return 'error';
    case 'expired': return 'warning';
    default: return 'neutral';
  }
};

export const QuotesPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentUser = useCurrentUser();
  const isAdmin = currentUser?.hasRole('Admin') ?? false;
  const [items, setItems] = useState<QuotesListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [totalItems, setTotalItems] = useState(0);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<QuoteType | ''>('');
  const [statusFilter, setStatusFilter] = useState<QuoteStatus[]>(
    DEFAULT_STATUS_FILTER,
  );
  const [createOpen, setCreateOpen] = useState(false);
  const [quickOpen, setQuickOpen] = useState(false);
  // When set, the list is scoped to one variation set (siblings sharing a
  // variationGroupId). Seeded from the ?variationGroupId= URL param (the
  // "Variation set" chip on the detail page links here) and cleared via the
  // banner's "Show all" action.
  const [variationGroupFilter, setVariationGroupFilterState] = useState<
    string | null
  >(searchParams.get('variationGroupId'));

  // Keep state + URL in sync so the scoped view is shareable and the back
  // button behaves.
  const setVariationGroupFilter = useCallback(
    (groupId: string | null) => {
      setVariationGroupFilterState(groupId);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (groupId) next.set('variationGroupId', groupId);
          else next.delete('variationGroupId');
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );
  // Row action overflow menu.
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuItem, setMenuItem] = useState<QuotesListItem | null>(null);
  const [duplicateSource, setDuplicateSource] = useState<QuotesListItem | null>(
    null,
  );
  const [actionBusy, setActionBusy] = useState(false);

  const closeMenu = useCallback(() => {
    setMenuAnchor(null);
    setMenuItem(null);
  }, []);

  const handleCreateVariation = useCallback(
    async (item: QuotesListItem) => {
      closeMenu();
      setActionBusy(true);
      setError(null);
      try {
        const created = await createQuoteVariation(item.number);
        navigate(`/quote/${created.number}`);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setActionBusy(false);
      }
    },
    [closeMenu, navigate],
  );

  const toggleStatus = useCallback((status: QuoteStatus) => {
    setStatusFilter((prev) =>
      prev.includes(status)
        ? prev.filter((s) => s !== status)
        : [...prev, status],
    );
  }, []);

  useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => window.clearTimeout(handle);
  }, [search]);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, typeFilter, statusFilter, variationGroupFilter]);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    fetchQuotes({
      page: page + 1,
      pageSize,
      search: debouncedSearch || undefined,
      type: typeFilter || undefined,
      // When scoped to a variation set we want every sibling regardless of
      // its workflow status, so the status filter is bypassed.
      status:
        variationGroupFilter || statusFilter.length === 0
          ? undefined
          : statusFilter,
      variationGroupId: variationGroupFilter || undefined,
      sort: 'createdAt',
      order: 'desc',
      signal: controller.signal,
    })
      .then((res) => {
        setItems(res.items);
        setTotalItems(res.pagination.totalItems);
      })
      .catch((err) => {
        if ((err as { name?: string }).name === 'AbortError') return;
        setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [
    page,
    pageSize,
    debouncedSearch,
    typeFilter,
    statusFilter,
    variationGroupFilter,
  ]);

  const skeletonRows = useMemo(
    () =>
      Array.from({ length: 5 }).map((_, i) => (
        <DataTableRow key={`sk-${i}`}>
          {Array.from({ length: 11 }).map((__, j) => (
            <DataTableCell key={j}>
              <Skeleton width="80%" />
            </DataTableCell>
          ))}
        </DataTableRow>
      )),
    [],
  );

  const typeOptions = useMemo(
    () => [
      { value: '', label: 'All types' },
      ...QUOTE_TYPE_OPTIONS.map((t) => ({
        value: t,
        label: QUOTE_TYPE_LABEL[t],
      })),
    ],
    [],
  );

  return (
    <>
      {/* Contextual toolbar — floating card on the grey page surface
          (main-content--quotes), matching the Invoices page. */}
      <SecondaryToolbar floating>
        <ToolbarSearch
          searchValue={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search quotes…"
        />
        <ToolbarSelect
          label="Type"
          value={typeFilter}
          onChange={(v) => setTypeFilter(v as QuoteType | '')}
          options={typeOptions}
        />
        {QUOTE_STATUS_FILTERS.map((s) => (
          <ToolbarCheckbox
            key={s}
            label={QUOTE_STATUS_LABEL[s]}
            active={statusFilter.includes(s)}
            onClick={() => toggleStatus(s)}
          />
        ))}
        <ToolbarSpacer />
        <ToolbarDivider />
        <ToolbarButton
          variant="primary"
          icon={IconPlus}
          onClick={() => setCreateOpen(true)}
        >
          New quote
        </ToolbarButton>
        {isAdmin && (
          <ToolbarButton
            variant="secondary"
            icon={IconBolt}
            onClick={() => setQuickOpen(true)}
          >
            Quick quote
          </ToolbarButton>
        )}
      </SecondaryToolbar>

      <Box sx={{ p: '12px 16px 24px', maxWidth: 1600, mx: 'auto' }}>
        {variationGroupFilter && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          icon={<CallSplit fontSize="inherit" />}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => setVariationGroupFilter(null)}
            >
              Show all
            </Button>
          }
        >
          Showing one variation set (all statuses).
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <DataTableCard>
        <DataTable>
          <thead>
            <tr>
              <DataTableHeadCell>Number</DataTableHeadCell>
              <DataTableHeadCell>Name</DataTableHeadCell>
              <DataTableHeadCell>Customer</DataTableHeadCell>
              <DataTableHeadCell>Type</DataTableHeadCell>
              <DataTableHeadCell>Status</DataTableHeadCell>
              <DataTableHeadCell>Invoice</DataTableHeadCell>
              <DataTableHeadCell>Parts</DataTableHeadCell>
              <DataTableHeadCell>Total</DataTableHeadCell>
              <DataTableHeadCell>Valid until</DataTableHeadCell>
              <DataTableHeadCell>Created</DataTableHeadCell>
              <DataTableHeadCell align="right" />
            </tr>
          </thead>
          <tbody>
            {loading
              ? skeletonRows
              : items.map((item) => (
                  <DataTableRow
                    key={item.id}
                    onClick={() => navigate(`/quote/${item.number}`)}
                  >
                    <DataTableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {item.number}
                        {item.variationGroupId && (
                          <Tooltip title="Part of a variation set — show siblings">
                            <IconButton
                              size="small"
                              sx={{ p: 0.25 }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setVariationGroupFilter(item.variationGroupId);
                              }}
                            >
                              <CallSplit sx={{ fontSize: '0.95rem' }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </DataTableCell>
                    <DataTableCell>{item.name}</DataTableCell>
                    <DataTableCell>{item.customerName}</DataTableCell>
                    <DataTableCell>{QUOTE_TYPE_LABEL[item.type]}</DataTableCell>
                    <DataTableCell>
                      <Chip tone={statusTone(item.status)}>{item.status}</Chip>
                    </DataTableCell>
                    <DataTableCell>
                      {item.invoiceNumber ? (
                        <Chip tone={item.invoicePaid ? 'success' : 'warning'}>
                          {item.invoicePaid
                            ? `${item.invoiceNumber} · Paid`
                            : `${item.invoiceNumber} · Unpaid`}
                        </Chip>
                      ) : (
                        '—'
                      )}
                    </DataTableCell>
                    <DataTableCell>
                      {item.partOrderRaised ? <Chip tone="info">Raised</Chip> : '—'}
                    </DataTableCell>
                    <DataTableCell>{fmtCurrency(item.total)}</DataTableCell>
                    <DataTableCell>{fmtDate(item.validUntil)}</DataTableCell>
                    <DataTableCell>{fmtDate(item.createdAt)}</DataTableCell>
                    <DataTableCell align="right">
                      <Tooltip title="Quote actions">
                        <IconButton
                          size="small"
                          disabled={actionBusy}
                          onClick={(e) => {
                            e.stopPropagation();
                            setMenuItem(item);
                            setMenuAnchor(e.currentTarget);
                          }}
                        >
                          <MoreVert fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </DataTableCell>
                  </DataTableRow>
                ))}
            {!loading && items.length === 0 && (
              <DataTableEmpty colSpan={11}>No quotes yet.</DataTableEmpty>
            )}
          </tbody>
        </DataTable>
        <DataTablePagination
          page={page}
          rowsPerPage={pageSize}
          count={totalItems}
          rowsPerPageOptions={[25, 50, 100]}
          onPageChange={setPage}
          onRowsPerPageChange={(rpp) => {
            setPageSize(rpp);
            setPage(0);
          }}
        />
      </DataTableCard>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={() => {
            if (menuItem) handleCreateVariation(menuItem);
          }}
        >
          <ListItemIcon>
            <CallSplit fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Create variation"
            secondary="Same customer"
          />
        </MenuItem>
        <MenuItem
          onClick={() => {
            setDuplicateSource(menuItem);
            closeMenu();
          }}
        >
          <ListItemIcon>
            <ContentCopy fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Duplicate…"
            secondary="Different customer"
          />
        </MenuItem>
      </Menu>

      <DuplicateQuoteDialog
        source={duplicateSource}
        onClose={() => setDuplicateSource(null)}
        onDuplicated={(number) => {
          setDuplicateSource(null);
          navigate(`/quote/${number}`);
        }}
      />

      <NewQuoteDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(number) => {
          setCreateOpen(false);
          navigate(`/quote/${number}`);
        }}
      />

      <QuickQuoteDialog
        open={quickOpen}
        onClose={() => setQuickOpen(false)}
        onCreated={(number) => {
          setQuickOpen(false);
          navigate(`/quote/${number}`);
        }}
      />
      </Box>
    </>
  );
};

interface NewQuoteDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: (quoteNumber: string) => void;
}

const NewQuoteDialog: React.FC<NewQuoteDialogProps> = ({
  open,
  onClose,
  onCreated,
}) => {
  const [customer, setCustomer] = useState<CustomerIndexItemDto | null>(null);
  const [type, setType] = useState<QuoteType>('new_laptop');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = useCallback(() => {
    setCustomer(null);
    setType('new_laptop');
    setError(null);
    onClose();
  }, [onClose]);

  const handleCreate = async () => {
    if (!customer) return;
    setSubmitting(true);
    setError(null);
    try {
      const quote = await createQuote({
        customerId: String(customer.dataverseCustomerId),
        type,
        // GST display defaults to inc-GST server-side; the tech can flip
        // a quote to ex-GST on the detail page when quoting a business.
      });
      onCreated(quote.number);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>New Quote</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 1 }}>
          Select a quote type and customer to start a draft.
        </Typography>
        <TextField
          select
          label="Quote type"
          value={type}
          onChange={(e) => setType(e.target.value as QuoteType)}
          fullWidth
          size="small"
          disabled={submitting}
          sx={{ mb: 2 }}
        >
          {QUOTE_TYPE_OPTIONS.map((t) => (
            <MenuItem key={t} value={t}>
              {QUOTE_TYPE_LABEL[t]}
            </MenuItem>
          ))}
        </TextField>
        <CustomerLookupControl
          value={customer}
          onChange={setCustomer}
          disabled={submitting}
        />
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleCreate}
          disabled={!customer || submitting}
        >
          {submitting ? <CircularProgress size={16} /> : 'Create Quote'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QuotesPage;
